import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Trophy, Play, RotateCcw, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Crown } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useGameLeaderboard } from '../hooks/useGameLeaderboard';
import { useNostrProfile } from '../hooks/useNostrQuery';

// ── Constants ────────────────────────────────────────────────────
const CELL = 20;       // pixel size of one cell
const TICK = 120;      // ms per game tick
const COLS = 21;
const ROWS = 23;

// Directions
const DIR = { UP: 0, RIGHT: 1, DOWN: 2, LEFT: 3 } as const;
type Dir = typeof DIR[keyof typeof DIR];
const DX = [0, 1, 0, -1];
const DY = [-1, 0, 1, 0];

// ── Map Layout ──────────────────────────────────────────────────
// W = wall, . = dot, o = power pellet, G = ghost house, P = pacman start
// - = ghost gate, E = empty
const MAP_TEMPLATE = [
  'WWWWWWWWWWWWWWWWWWWWW',
  'W.........W.........W',
  'W.WWW.WWW.W.WWW.WWW.W',
  'WoW W.W W.W.W W.W WoW',
  'W.WWW.WWW.W.WWW.WWW.W',
  'W...................W',
  'W.WWW.W.WWWWW.W.WWW.W',
  'W.....W...W...W.....W',
  'WWWWW.WWW.W.WWW.WWWWW',
  'EEEEW.W.......W.WEEEE',
  'WWWWW.W.WW-WW.W.WWWWW',
  'EEEEE...WGGGW...EEEEE',
  'WWWWW.W.WGGGW.W.WWWWW',
  'EEEEW.W.WWWWW.W.WEEEE',
  'WWWWW.W.......W.WWWWW',
  'W.........W.........W',
  'W.WWW.WWW.W.WWW.WWW.W',
  'Wo..W...........W..oW',
  'WWW.W.W.WWWWW.W.W.WWW',
  'W.....W...W...W.....W',
  'W.WWWWWWW.W.WWWWWWW.W',
  'W...................W',
  'WWWWWWWWWWWWWWWWWWWWW',
];

function parseMap() {
  const walls: boolean[][] = [];
  const dots: boolean[][] = [];
  const powers: boolean[][] = [];
  let pacStart = { x: 10, y: 17 };
  const ghostStarts = [
    { x: 9, y: 11 }, { x: 10, y: 11 }, { x: 11, y: 11 }, { x: 10, y: 13 }
  ];

  for (let y = 0; y < ROWS; y++) {
    walls[y] = [];
    dots[y] = [];
    powers[y] = [];
    for (let x = 0; x < COLS; x++) {
      const ch = MAP_TEMPLATE[y]?.[x] || 'W';
      walls[y][x] = ch === 'W';
      dots[y][x] = ch === '.' || ch === 'P';
      powers[y][x] = ch === 'o';
      if (ch === 'P') pacStart = { x, y };
    }
  }

  return { walls, dots, powers, pacStart, ghostStarts };
}

interface Ghost {
  x: number;
  y: number;
  dir: Dir;
  scared: boolean;
  eaten: boolean;
  color: string;
}

interface GameState {
  pacX: number;
  pacY: number;
  pacDir: Dir;
  nextDir: Dir;
  mouthOpen: boolean;
  ghosts: Ghost[];
  dots: boolean[][];
  powers: boolean[][];
  score: number;
  lives: number;
  level: number;
  gameOver: boolean;
  won: boolean;
  paused: boolean;
  started: boolean;
  scaredTimer: number;
  dotCount: number;
  totalDots: number;
}

function initGame(level = 1): GameState {
  const { walls, dots, powers, pacStart, ghostStarts } = parseMap();

  let totalDots = 0;
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      if (dots[y][x] || powers[y][x]) totalDots++;

  const ghostColors = ['#ff0000', '#ffb8ff', '#00ffff', '#ffb852'];
  const ghosts: Ghost[] = ghostStarts.map((g, i) => ({
    x: g.x, y: g.y, dir: DIR.UP, scared: false, eaten: false, color: ghostColors[i],
  }));

  return {
    pacX: pacStart.x,
    pacY: pacStart.y,
    pacDir: DIR.LEFT,
    nextDir: DIR.LEFT,
    mouthOpen: true,
    ghosts,
    dots,
    powers,
    score: 0,
    lives: 3,
    level,
    gameOver: false,
    won: false,
    paused: false,
    started: false,
    scaredTimer: 0,
    dotCount: 0,
    totalDots,
  };
}

function getWalls() {
  return parseMap().walls;
}

// ── Game logic ──────────────────────────────────────────────────

function canMove(walls: boolean[][], x: number, y: number): boolean {
  // Tunnel wrapping
  if (x < 0 || x >= COLS) return true;
  if (y < 0 || y >= ROWS) return false;
  return !walls[y][x];
}

function wrapX(x: number): number {
  if (x < 0) return COLS - 1;
  if (x >= COLS) return 0;
  return x;
}

function tick(state: GameState, walls: boolean[][]): GameState {
  if (state.gameOver || state.won || state.paused || !state.started) return state;

  let s = { ...state, dots: state.dots.map(r => [...r]), powers: state.powers.map(r => [...r]) };
  s.mouthOpen = !s.mouthOpen;

  // ── Move Pac-Man ──
  // Try the buffered direction first
  const nx1 = wrapX(s.pacX + DX[s.nextDir]);
  const ny1 = s.pacY + DY[s.nextDir];
  if (canMove(walls, nx1, ny1)) {
    s.pacDir = s.nextDir;
    s.pacX = nx1;
    s.pacY = ny1;
  } else {
    // Continue in current direction
    const nx2 = wrapX(s.pacX + DX[s.pacDir]);
    const ny2 = s.pacY + DY[s.pacDir];
    if (canMove(walls, nx2, ny2)) {
      s.pacX = nx2;
      s.pacY = ny2;
    }
  }

  // ── Eat dots ──
  if (s.pacY >= 0 && s.pacY < ROWS && s.pacX >= 0 && s.pacX < COLS) {
    if (s.dots[s.pacY][s.pacX]) {
      s.dots[s.pacY][s.pacX] = false;
      s.score += 10;
      s.dotCount++;
    }
    if (s.powers[s.pacY][s.pacX]) {
      s.powers[s.pacY][s.pacX] = false;
      s.score += 50;
      s.dotCount++;
      s.scaredTimer = 40; // ~5 seconds
      s.ghosts = s.ghosts.map(g => ({ ...g, scared: !g.eaten, eaten: false }));
    }
  }

  // ── Scared timer ──
  if (s.scaredTimer > 0) {
    s.scaredTimer--;
    if (s.scaredTimer === 0) {
      s.ghosts = s.ghosts.map(g => ({ ...g, scared: false }));
    }
  }

  // ── Move ghosts ──
  const speed = Math.min(s.level, 5);
  s.ghosts = s.ghosts.map((ghost, gi) => {
    let g = { ...ghost };

    // Simple AI: try to move toward pac-man with some randomness
    const possibleDirs: Dir[] = [];
    const opposite = ((g.dir + 2) % 4) as Dir;
    for (let d = 0; d < 4; d++) {
      if (d === opposite) continue; // don't reverse
      const gx = wrapX(g.x + DX[d]);
      const gy = g.y + DY[d];
      if (canMove(walls, gx, gy) && !(MAP_TEMPLATE[gy]?.[gx] === 'E')) {
        possibleDirs.push(d as Dir);
      }
    }

    if (possibleDirs.length === 0) {
      // Dead end — reverse
      possibleDirs.push(opposite);
    }

    // Choose direction
    let chosenDir: Dir;
    if (g.scared || g.eaten) {
      // Run away / random
      chosenDir = possibleDirs[Math.floor(Math.random() * possibleDirs.length)];
    } else {
      // Chase pac-man (with 30% randomness for variety)
      if (Math.random() < 0.3 + (gi * 0.05)) {
        chosenDir = possibleDirs[Math.floor(Math.random() * possibleDirs.length)];
      } else {
        // Pick direction that reduces distance to pac-man
        let bestDir = possibleDirs[0];
        let bestDist = Infinity;
        for (const d of possibleDirs) {
          const gx = wrapX(g.x + DX[d]);
          const gy = g.y + DY[d];
          const dist = Math.abs(gx - s.pacX) + Math.abs(gy - s.pacY);
          if (dist < bestDist) {
            bestDist = dist;
            bestDir = d;
          }
        }
        chosenDir = bestDir;
      }
    }

    const newX = wrapX(g.x + DX[chosenDir]);
    const newY = g.y + DY[chosenDir];

    if (canMove(walls, newX, newY)) {
      g.x = newX;
      g.y = newY;
      g.dir = chosenDir;
    }

    return g;
  });

  // ── Collision detection ──
  for (let i = 0; i < s.ghosts.length; i++) {
    if (s.ghosts[i].x === s.pacX && s.ghosts[i].y === s.pacY) {
      if (s.ghosts[i].scared) {
        // Eat ghost
        s.ghosts = s.ghosts.map((g, gi) =>
          gi === i ? { ...g, scared: false, eaten: true, x: 10, y: 11 } : g
        );
        s.score += 200;
      } else if (!s.ghosts[i].eaten) {
        // Pac-Man dies
        s.lives--;
        if (s.lives <= 0) {
          s.gameOver = true;
        } else {
          // Reset positions
          const { pacStart, ghostStarts } = parseMap();
          s.pacX = pacStart.x;
          s.pacY = pacStart.y;
          s.pacDir = DIR.LEFT;
          s.nextDir = DIR.LEFT;
          const ghostColors = ['#ff0000', '#ffb8ff', '#00ffff', '#ffb852'];
          s.ghosts = ghostStarts.map((gs, gi) => ({
            x: gs.x, y: gs.y, dir: DIR.UP, scared: false, eaten: false, color: ghostColors[gi],
          }));
          s.scaredTimer = 0;
        }
        break;
      }
    }
  }

  // ── Win condition ──
  if (s.dotCount >= s.totalDots) {
    s.won = true;
    s.score += 1000 * s.level;
  }

  return s;
}

// ── Canvas Rendering ────────────────────────────────────────────

function drawGame(ctx: CanvasRenderingContext2D, state: GameState, walls: boolean[][]) {
  const w = COLS * CELL;
  const h = ROWS * CELL;

  // Background
  ctx.fillStyle = '#0a0a1a';
  ctx.fillRect(0, 0, w, h);

  // Walls
  ctx.fillStyle = '#1a3a6a';
  ctx.strokeStyle = '#3b82f6';
  ctx.lineWidth = 1.5;
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      if (walls[y][x]) {
        const px = x * CELL;
        const py = y * CELL;
        ctx.fillRect(px, py, CELL, CELL);
        // Draw border only on sides adjacent to non-wall
        const top = y > 0 && !walls[y - 1]?.[x];
        const bottom = y < ROWS - 1 && !walls[y + 1]?.[x];
        const left = x > 0 && !walls[y][x - 1];
        const right = x < COLS - 1 && !walls[y][x + 1];
        ctx.beginPath();
        if (top) { ctx.moveTo(px, py + 0.5); ctx.lineTo(px + CELL, py + 0.5); }
        if (bottom) { ctx.moveTo(px, py + CELL - 0.5); ctx.lineTo(px + CELL, py + CELL - 0.5); }
        if (left) { ctx.moveTo(px + 0.5, py); ctx.lineTo(px + 0.5, py + CELL); }
        if (right) { ctx.moveTo(px + CELL - 0.5, py); ctx.lineTo(px + CELL - 0.5, py + CELL); }
        ctx.stroke();
      }
    }
  }

  // Ghost gate
  ctx.strokeStyle = '#ff88ff';
  ctx.lineWidth = 2;
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      if (MAP_TEMPLATE[y]?.[x] === '-') {
        ctx.beginPath();
        ctx.moveTo(x * CELL, y * CELL + CELL / 2);
        ctx.lineTo(x * CELL + CELL, y * CELL + CELL / 2);
        ctx.stroke();
      }
    }
  }

  // Dots (BCH coins)
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      if (state.dots[y][x]) {
        const cx = x * CELL + CELL / 2;
        const cy = y * CELL + CELL / 2;
        ctx.fillStyle = '#4ade80';
        ctx.beginPath();
        ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // Power pellets (big BCH coins, pulsing)
  const pulse = Math.sin(Date.now() / 200) * 0.3 + 0.7;
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      if (state.powers[y][x]) {
        const cx = x * CELL + CELL / 2;
        const cy = y * CELL + CELL / 2;
        ctx.fillStyle = `rgba(74, 222, 128, ${pulse})`;
        ctx.beginPath();
        ctx.arc(cx, cy, 6, 0, Math.PI * 2);
        ctx.fill();
        // BCH logo hint
        ctx.fillStyle = '#0a0a1a';
        ctx.font = 'bold 8px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('B', cx, cy + 0.5);
      }
    }
  }

  // Ghosts
  for (const ghost of state.ghosts) {
    const gx = ghost.x * CELL + CELL / 2;
    const gy = ghost.y * CELL + CELL / 2;
    const r = CELL / 2 - 1;

    if (ghost.eaten) {
      // Just eyes
      drawGhostEyes(ctx, gx, gy, ghost.dir);
      continue;
    }

    // Body
    const flickering = state.scaredTimer > 0 && state.scaredTimer < 10 && Math.floor(Date.now() / 200) % 2 === 0;
    ctx.fillStyle = ghost.scared
      ? (flickering ? '#ffffff' : '#2222ff')
      : ghost.color;
    ctx.beginPath();
    ctx.arc(gx, gy - 2, r, Math.PI, 0);
    ctx.lineTo(gx + r, gy + r);
    // Wavy bottom
    const waves = 3;
    const waveW = (r * 2) / waves;
    for (let i = 0; i < waves; i++) {
      const wx = gx + r - (i + 1) * waveW;
      ctx.quadraticCurveTo(wx + waveW / 2, gy + r - 4, wx, gy + r);
    }
    ctx.closePath();
    ctx.fill();

    // Eyes
    if (!ghost.scared) {
      drawGhostEyes(ctx, gx, gy, ghost.dir);
    } else {
      // Scared face
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(gx - 3, gy - 3, 2, 0, Math.PI * 2);
      ctx.arc(gx + 3, gy - 3, 2, 0, Math.PI * 2);
      ctx.fill();
      // Squiggly mouth
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(gx - 4, gy + 2);
      for (let i = 0; i < 4; i++) {
        ctx.lineTo(gx - 4 + i * 3, gy + (i % 2 === 0 ? 2 : 5));
      }
      ctx.stroke();
    }
  }

  // Pac-Man
  {
    const px = state.pacX * CELL + CELL / 2;
    const py = state.pacY * CELL + CELL / 2;
    const r = CELL / 2 - 1;
    const mouthAngle = state.mouthOpen ? 0.3 : 0.05;
    const angles = [0, Math.PI / 2, Math.PI, -Math.PI / 2]; // right, down, left, up
    const baseAngle = angles[state.pacDir] ?? 0;

    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.arc(px, py, r, baseAngle + mouthAngle * Math.PI, baseAngle - mouthAngle * Math.PI + Math.PI * 2);
    ctx.closePath();
    ctx.fill();

    // Eye
    const eyeAngle = baseAngle - Math.PI / 4;
    const eyeX = px + Math.cos(eyeAngle) * r * 0.4;
    const eyeY = py + Math.sin(eyeAngle) * r * 0.4;
    ctx.fillStyle = '#0a0a1a';
    ctx.beginPath();
    ctx.arc(eyeX, eyeY, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Lives
  for (let i = 0; i < state.lives - 1; i++) {
    const lx = 16 + i * 20;
    const ly = ROWS * CELL + 12;
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.arc(lx, ly, 7, 0.2 * Math.PI, -0.2 * Math.PI + Math.PI * 2);
    ctx.lineTo(lx, ly);
    ctx.closePath();
    ctx.fill();
  }
}

function drawGhostEyes(ctx: CanvasRenderingContext2D, gx: number, gy: number, dir: Dir) {
  const eyeOffX = DX[dir] * 2;
  const eyeOffY = DY[dir] * 2;

  // Eye whites
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.ellipse(gx - 3, gy - 3, 3, 4, 0, 0, Math.PI * 2);
  ctx.ellipse(gx + 3, gy - 3, 3, 4, 0, 0, Math.PI * 2);
  ctx.fill();

  // Pupils
  ctx.fillStyle = '#0000aa';
  ctx.beginPath();
  ctx.arc(gx - 3 + eyeOffX, gy - 3 + eyeOffY, 1.5, 0, Math.PI * 2);
  ctx.arc(gx + 3 + eyeOffX, gy - 3 + eyeOffY, 1.5, 0, Math.PI * 2);
  ctx.fill();
}

// ── High Score Tag for Nostr ────────────────────────────────────
const PACMAN_TAG = 'bchnostr-pacman';

// ── Component ───────────────────────────────────────────────────

export default function PacManPage() {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<GameState>(() => initGame());
  const stateRef = useRef(state);
  stateRef.current = state;
  const wallsRef = useRef(getWalls());
  const [highScore, setHighScore] = useState(() => {
    try { return parseInt(localStorage.getItem('bchnostr:pacman-high') || '0'); } catch { return 0; }
  });
  const [showMobileControls, setShowMobileControls] = useState(false);

  // Detect touch device
  useEffect(() => {
    setShowMobileControls('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Keyboard input
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const key = e.key;
      let dir: Dir | null = null;
      if (key === 'ArrowUp' || key === 'w' || key === 'W') dir = DIR.UP;
      if (key === 'ArrowRight' || key === 'd' || key === 'D') dir = DIR.RIGHT;
      if (key === 'ArrowDown' || key === 's' || key === 'S') dir = DIR.DOWN;
      if (key === 'ArrowLeft' || key === 'a' || key === 'A') dir = DIR.LEFT;

      if (dir !== null) {
        e.preventDefault();
        setState(prev => {
          if (!prev.started) return { ...prev, started: true, nextDir: dir! };
          return { ...prev, nextDir: dir! };
        });
      }

      if (key === ' ' || key === 'p' || key === 'P') {
        e.preventDefault();
        setState(prev => prev.started ? { ...prev, paused: !prev.paused } : prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Touch swipe support
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX, y: t.clientY };
    };
    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartRef.current.x;
      const dy = t.clientY - touchStartRef.current.y;
      const threshold = 20;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > threshold) {
        setState(prev => {
          const dir = dx > 0 ? DIR.RIGHT : DIR.LEFT;
          if (!prev.started) return { ...prev, started: true, nextDir: dir };
          return { ...prev, nextDir: dir };
        });
      } else if (Math.abs(dy) > threshold) {
        setState(prev => {
          const dir = dy > 0 ? DIR.DOWN : DIR.UP;
          if (!prev.started) return { ...prev, started: true, nextDir: dir };
          return { ...prev, nextDir: dir };
        });
      }
      touchStartRef.current = null;
    };
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Game loop
  useEffect(() => {
    const interval = setInterval(() => {
      setState(prev => tick(prev, wallsRef.current));
    }, TICK);
    return () => clearInterval(interval);
  }, []);

  // Render loop
  useEffect(() => {
    let raf: number;
    const render = () => {
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) drawGame(ctx, stateRef.current, wallsRef.current);
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Nostr leaderboard
  const { scores: leaderboard, loading: lbLoading, submitScore: submitLeaderboardScore, myScore: myLeaderboardScore } = useGameLeaderboard('pacman', user?.pubkey);

  // High score tracking & Nostr posting
  useEffect(() => {
    if ((state.gameOver || state.won) && state.score > highScore) {
      setHighScore(state.score);
      try { localStorage.setItem('bchnostr:pacman-high', String(state.score)); } catch {}
    }
  }, [state.gameOver, state.won, state.score, highScore]);

  // Auto-submit score to leaderboard when game ends
  useEffect(() => {
    if ((state.gameOver || state.won) && user && state.score > 0) {
      submitLeaderboardScore(state.score, { level: state.level });
    }
  }, [state.gameOver, state.won]);

  const shareScore = async () => {
    if (!user) return;
    try {
      await publish({
        kind: 1,
        content: `I scored ${state.score} points in BCH Pac-Man on bchnostr.com/games/pacman! Can you beat my score? ${state.won ? 'Cleared level ' + state.level + '!' : ''} #BCHNostr #PacMan`,
        tags: [
          ['t', PACMAN_TAG],
          ['t', 'bch'],
          ['t', 'bitcoincash'],
          ['t', 'gaming'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {}
  };

  const handleRestart = () => {
    wallsRef.current = getWalls();
    setState(initGame());
  };

  const handleNextLevel = () => {
    wallsRef.current = getWalls();
    setState(prev => initGame(prev.level + 1));
  };

  const handleDirButton = (dir: Dir) => {
    setState(prev => {
      if (!prev.started) return { ...prev, started: true, nextDir: dir };
      return { ...prev, nextDir: dir };
    });
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Link to="/games" className="p-1 rounded-lg hover:bg-muted -ml-1">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" /> BCH Pac-Man
          </h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Score bar */}
        <div className="flex items-center justify-between px-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Score</p>
            <p className="text-lg font-bold tabular-nums text-primary">{state.score.toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Level</p>
            <p className="text-lg font-bold tabular-nums">{state.level}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">High Score</p>
            <p className="text-lg font-bold tabular-nums text-amber-500">{highScore.toLocaleString()}</p>
          </div>
        </div>

        {/* Canvas */}
        <div className="relative flex justify-center">
          <canvas
            ref={canvasRef}
            width={COLS * CELL}
            height={ROWS * CELL + 24}
            className="rounded-xl border border-border shadow-lg touch-none"
            style={{ imageRendering: 'pixelated', maxWidth: '100%', height: 'auto' }}
          />

          {/* Start overlay */}
          {!state.started && !state.gameOver && !state.won && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center rounded-xl">
              <div className="text-5xl mb-4">
                <span className="text-yellow-400">ᗧ</span>
                <span className="ml-1 text-red-500">ᗣ</span>
                <span className="ml-1 text-cyan-400">ᗣ</span>
                <span className="ml-1 text-pink-400">ᗣ</span>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">BCH Pac-Man</h2>
              <p className="text-sm text-white/60 mb-4">Collect all the BCH coins!</p>
              <button
                onClick={() => setState(prev => ({ ...prev, started: true }))}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 transition-colors"
              >
                <Play className="w-5 h-5" /> Start Game
              </button>
              <p className="text-[10px] text-white/40 mt-3">Arrow keys / WASD / Swipe to move</p>
            </div>
          )}

          {/* Paused overlay */}
          {state.paused && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center rounded-xl">
              <p className="text-2xl font-bold text-white mb-4">PAUSED</p>
              <button
                onClick={() => setState(prev => ({ ...prev, paused: false }))}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
              >
                <Play className="w-5 h-5" /> Resume
              </button>
            </div>
          )}

          {/* Game Over overlay */}
          {state.gameOver && (
            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center rounded-xl">
              <p className="text-3xl font-bold text-red-500 mb-2">GAME OVER</p>
              <p className="text-lg text-white mb-1">Score: <span className="text-primary font-bold">{state.score.toLocaleString()}</span></p>
              {state.score >= highScore && state.score > 0 && (
                <p className="text-sm text-amber-400 font-semibold mb-3 flex items-center gap-1">
                  <Trophy className="w-4 h-4" /> New High Score!
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleRestart}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
                >
                  <RotateCcw className="w-4 h-4" /> Play Again
                </button>
                {user && (
                  <button
                    onClick={shareScore}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-white font-bold text-sm"
                  >
                    <Trophy className="w-4 h-4" /> Share Score
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Win overlay */}
          {state.won && (
            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center rounded-xl">
              <p className="text-3xl font-bold text-emerald-400 mb-2">LEVEL CLEAR!</p>
              <p className="text-lg text-white mb-1">Score: <span className="text-primary font-bold">{state.score.toLocaleString()}</span></p>
              {state.score >= highScore && state.score > 0 && (
                <p className="text-sm text-amber-400 font-semibold mb-3 flex items-center gap-1">
                  <Trophy className="w-4 h-4" /> New High Score!
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleNextLevel}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-white font-bold text-sm"
                >
                  <Play className="w-4 h-4" /> Level {state.level + 1}
                </button>
                {user && (
                  <button
                    onClick={shareScore}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-white font-bold text-sm"
                  >
                    <Trophy className="w-4 h-4" /> Share Score
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mobile D-pad controls */}
        {showMobileControls && (
          <div className="flex justify-center pt-2">
            <div className="grid grid-cols-3 gap-1 w-40">
              <div />
              <button
                onTouchStart={(e) => { e.preventDefault(); handleDirButton(DIR.UP); }}
                onClick={() => handleDirButton(DIR.UP)}
                className="aspect-square rounded-xl bg-muted border border-border flex items-center justify-center active:bg-primary/20 active:border-primary/40 transition-colors"
              >
                <ArrowUp className="w-6 h-6" />
              </button>
              <div />
              <button
                onTouchStart={(e) => { e.preventDefault(); handleDirButton(DIR.LEFT); }}
                onClick={() => handleDirButton(DIR.LEFT)}
                className="aspect-square rounded-xl bg-muted border border-border flex items-center justify-center active:bg-primary/20 active:border-primary/40 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="aspect-square rounded-xl bg-muted/30 border border-border/30 flex items-center justify-center">
                <Crown className="w-4 h-4 text-muted-foreground/30" />
              </div>
              <button
                onTouchStart={(e) => { e.preventDefault(); handleDirButton(DIR.RIGHT); }}
                onClick={() => handleDirButton(DIR.RIGHT)}
                className="aspect-square rounded-xl bg-muted border border-border flex items-center justify-center active:bg-primary/20 active:border-primary/40 transition-colors"
              >
                <ArrowRight className="w-6 h-6" />
              </button>
              <div />
              <button
                onTouchStart={(e) => { e.preventDefault(); handleDirButton(DIR.DOWN); }}
                onClick={() => handleDirButton(DIR.DOWN)}
                className="aspect-square rounded-xl bg-muted border border-border flex items-center justify-center active:bg-primary/20 active:border-primary/40 transition-colors"
              >
                <ArrowDown className="w-6 h-6" />
              </button>
              <div />
            </div>
          </div>
        )}

        {/* Controls help */}
        <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-1">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Controls</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            <span>Move: Arrow keys / WASD</span>
            <span>Mobile: Swipe / D-pad</span>
            <span>Pause: Space / P</span>
            <span>Eat power pellets to chase ghosts!</span>
          </div>
        </div>

        {/* ── Leaderboard ── */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-gradient-to-r from-amber-500/10 to-transparent flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-bold">Leaderboard</h3>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {leaderboard.length > 0 ? `${leaderboard.length} player${leaderboard.length !== 1 ? 's' : ''}` : ''}
            </span>
          </div>

          {lbLoading && leaderboard.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Loading scores...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-3xl mb-2">🏆</p>
              <p className="text-sm font-medium">No scores yet!</p>
              <p className="text-xs text-muted-foreground mt-1">
                {user ? 'Play a game and your score will appear here.' : 'Log in and play to be the first on the board!'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {leaderboard.slice(0, 20).map((entry, i) => (
                <LeaderboardRow key={entry.pubkey} entry={entry} rank={i + 1} isMe={entry.pubkey === user?.pubkey} />
              ))}
            </div>
          )}

          {/* My rank (if not in top 20) */}
          {myLeaderboardScore && leaderboard.findIndex(s => s.pubkey === user?.pubkey) >= 20 && (
            <div className="border-t-2 border-dashed border-primary/30">
              <LeaderboardRow
                entry={myLeaderboardScore}
                rank={leaderboard.findIndex(s => s.pubkey === user?.pubkey) + 1}
                isMe
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Leaderboard Row ──────────────────────────────────────────────
function LeaderboardRow({ entry, rank, isMe }: { entry: import('../hooks/useGameLeaderboard').ScoreEntry; rank: number; isMe: boolean }) {
  const { profile } = useNostrProfile(entry.pubkey);
  const name = profile?.name || profile?.display_name || entry.pubkey.slice(0, 8) + '...';
  const avatar = profile?.picture;

  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;

  return (
    <Link
      to={`/profile/${entry.pubkey}`}
      className={`flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors ${isMe ? 'bg-primary/5' : ''}`}
    >
      {/* Rank */}
      <div className="w-7 text-center shrink-0">
        {medal ? (
          <span className="text-base">{medal}</span>
        ) : (
          <span className={`text-xs font-bold tabular-nums ${isMe ? 'text-primary' : 'text-muted-foreground'}`}>{rank}</span>
        )}
      </div>

      {/* Avatar */}
      <div className="w-8 h-8 rounded-full bg-muted overflow-hidden shrink-0">
        {avatar ? (
          <img src={avatar} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs font-bold">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isMe ? 'text-primary' : ''}`}>
          {name}
          {isMe && <span className="text-[10px] text-primary/60 ml-1">(you)</span>}
        </p>
        {entry.level && entry.level > 1 && (
          <p className="text-[10px] text-muted-foreground">Level {entry.level}</p>
        )}
      </div>

      {/* Score */}
      <div className="text-right shrink-0">
        <p className={`text-sm font-bold tabular-nums ${rank <= 3 ? 'text-amber-500' : isMe ? 'text-primary' : ''}`}>
          {entry.score.toLocaleString()}
        </p>
      </div>
    </Link>
  );
}

